import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormGroup, FormControl } from '@angular/forms';

import { PreaggregationStrategy } from '../../shared/preaggregation/preaggregation-strategy';
import { PreaggregationSevice } from 'src/app/shared/preaggregation/preaggregation-service';
import { ProjectService } from 'src/app/shared/project/project.service';
import { ColumnForSelectionWithAltSelection } from 'src/app/shared/import/column-for-selection';
import { BaseColumnSelectComponent } from '../column-types/base-column-selection.component';
import { ColumnType } from 'src/app/shared/import/column-type';

@Component({
  templateUrl: './preaggregation.component.html',
  styleUrls: ['../general.scss', '../column-types/column-selection.scss']
})
export class PreaggregationStrategySelectComponent extends BaseColumnSelectComponent implements OnInit {
    PreaggregationStrategy: typeof PreaggregationStrategy = PreaggregationStrategy;   //hack for enum to be available in the template
    projectId: string;
    availableStrategies: PreaggregationStrategy[] = [];

    outcomeColumnName: string;
    volumeFormulaComponetColumnName: string;
    otherFormulaComponentsNames: string;
    oldControlColumnValue: string;
    newControlColumnValue: string;
    
    columns: ColumnForSelectionWithAltSelection[];

    public numOfCombinations: number;
    public numOfDimensions: number;
    public dimensionsNames: string;
    public numOfNotUniqueCombinations: number;
    public percOfNotUniqueCombinations: number;
    public numOfBothControlValuesCombinations: number;
    public percOfBothControlValuesCombinations: number;
    
    public weightColumnName: string;
    public weightedColumnNames: string;
    public summedColumnNames: string;
    public averagedColumnNames: string;

    loading = false;
    dataLoaded = false;
    selectingWeightColumn = false;
    selectingColumnsToSum = false;

    private wantBenchmarks = false;

    form = new FormGroup({
        strategy: new FormControl(),
    });

    constructor(private preaggregationSevice: PreaggregationSevice, private router: Router,
        private activatedRoute: ActivatedRoute, private projectService: ProjectService)  {
            super();
    }

    ngOnInit(): void {
        this.activatedRoute.queryParams.subscribe((queryParams: any) => {
            let projectId = queryParams.projectId;
            this.wantBenchmarks = queryParams.benchmarks === 'true';
            if (projectId) {
                this.projectId = projectId;
                this.projectService.setProjectId(projectId);
                this.preaggregationSevice.getPreaggregationCheckResult(projectId)
                .then(result => {
                    this.outcomeColumnName = result.outcomeColumnName;
                    this.volumeFormulaComponetColumnName = result.volumeFormulaComponetColumnName;
                    this.otherFormulaComponentsNames = result.otherFormulaComponentsNames;
                    this.oldControlColumnValue = result.oldControlColumnValue;
                    this.newControlColumnValue = result.newControlColumnValue;
                    this.availableStrategies = result.availablePreaggregationStrategies;
                    this.form.controls.strategy.setValue(result.selectedStrategy);

                    this.numOfCombinations = result.numOfCombinations;
                    this.numOfDimensions = result.numOfDimensions;
                    this.dimensionsNames = result.dimensionsNames;
                    this.numOfNotUniqueCombinations = result.numOfNotUniqueCombinations;
                    this.percOfNotUniqueCombinations = result.percOfNotUniqueCombinations;
                    this.numOfBothControlValuesCombinations = result.numOfBothControlValuesCombinations;
                    this.percOfBothControlValuesCombinations = result.percOfBothControlValuesCombinations;

                    this.columns = result.columns;
                    this.loadTexts();
                    
                    this.dataLoaded = true;
                });
            }
        });
    }

    async selectStrategy() {
        if (this.loading) {
            return;
        }
        try {
            this.loading = true;
            let selectedStrategy: PreaggregationStrategy = this.form.controls.strategy.value;
            if (selectedStrategy == PreaggregationStrategy.reselectDimensions) {
                this.router.navigateByUrl('/select-columns-dimensions?projectId=' + this.projectId);
                return;
            } else if (selectedStrategy == PreaggregationStrategy.reuploadFile) {
                this.router.navigateByUrl('/reupload?projectId=' + this.projectId);
                return;
            }
            await this.preaggregationSevice.savePreaggregationStrategy(this.projectId, selectedStrategy, this.columns);
            if (this.wantBenchmarks) {
                this.router.navigateByUrl('/series?projectId=' + this.projectId);
            } else {
                this.router.navigateByUrl('/cards?projectId=' + this.projectId);
            }
        } finally {
            this.loading = false;
        }
    }
    
    public columnClick(column: ColumnForSelectionWithAltSelection): void {
        if (!column || (this.selectingColumnsToSum && !column.isEligibleAlt) || (this.startSelectingWeightColumn && !column.isEligible)) {
            return;
        }
        let selectedStrategy: PreaggregationStrategy = this.form.controls.strategy.value;
        if (selectedStrategy == PreaggregationStrategy.averageAndSum) {
            if (this.selectingColumnsToSum) {
                column.isSelectedAlt = !column.isSelectedAlt || column.isForcedSelectedAlt;
            } else if (this.selectingWeightColumn) {
                let curVal = column.isSelected;
                for (var i = 0; i < this.columns.length; ++i) {
                    this.columns[i].isSelected = false;
                }
                column.isSelected = !curVal;
            }
        }
        this.loadTexts();
    }

    public radioChange($event) {
        this.loadTexts();
    }

    public startSelectingWeightColumn() {
        this.selectingWeightColumn = true;
        this.selectingColumnsToSum = false;
    }

    public startSelectingColumnsToSum() {
        this.selectingWeightColumn = false;
        this.selectingColumnsToSum = true;
    }

    private loadTexts() {
        if (this.columns && this.columns.length) {
            let weightColumn = this.columns.find(c => c.isSelected);
            this.weightColumnName = weightColumn ? '[' + weightColumn.name + ']' : undefined;
            this.weightedColumnNames = this.columns.filter(c => ! c.isSelected && c.type == ColumnType.formulaComponent) .map(c => '[' + c.name + ']').join(', ');
            this.summedColumnNames = this.columns.filter(c => c.isSelectedAlt).map(c => '[' + c.name + ']').join(', ');
            this.averagedColumnNames = this.columns.filter(c => !c.isSelectedAlt && c.type == ColumnType.formulaComponent) .map(c => '[' + c.name + ']').join(', ');
        }
    }
}
